import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  HStack,
  Input,
  Stack,
  Text,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { ControlledSelect } from '@resistapp/client/components/forms/form-inputs/controlled-select';
import { theme } from '@resistapp/client/components/shared/theme';
import { MaybeApiError } from '@resistapp/client/components/states/error';
import { LoadingView } from '@resistapp/client/components/states/loading';
import { isWaterType, SampleType } from '@resistapp/common/sampling-point-types';
import { AccessLevel, BioRep, PatchSampleRequest, SampleStatus, zPatchSampleRequest } from '@resistapp/common/types';
import { chain } from 'lodash';
import { Path, useForm } from 'react-hook-form';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { useAdminSample, useAdminSampleAccess, useAdminSampleChips, usePatchSample } from '../../hooks/api';

export function AdminSampleView() {
  const params = useParams();
  const idParam = params.id;
  const sampleId = idParam ? Number(idParam) : undefined;

  const { data: sample, isPending, error } = useAdminSample(sampleId);
  const { data: chips } = useAdminSampleChips(sampleId as number);
  const {
    data: sampleAccess,
    isPending: isSampleAccessPending,
    error: sampleAccessError,
  } = useAdminSampleAccess(sampleId);

  const organisationsWithAccess = sampleAccess?.organisations ?? [];

  if (isPending) {
    return <LoadingView />;
  }

  return (
    <div style={{ padding: theme.spacing[4], marginBottom: theme.spacing[16] }}>
      <Heading size="md" mb={theme.spacing[4]}>
        {sample ? `Sample #${sample.id}` : 'Sample'}
      </Heading>

      <Stack spacing={4} maxW={720} bg="white" p={theme.spacing[4]} borderRadius={6} boxShadow="sm">
        {sample ? (
          <>
            <Box>
              <Text fontWeight={600}>Sampling point</Text>
              <RouterLink to={`/admin/sampling-point/${sample.samplingPoint.id}`} style={{ fontWeight: 600 }}>
                {sample.samplingPoint.name} ({sample.samplingPoint.id})
              </RouterLink>
              <Text as="span" color="gray.600">{` · ${sample.samplingPoint.type}`}</Text>
            </Box>

            {sample.sampleSheet ? (
              <Box>
                <Text fontWeight={600}>SmartChip specification</Text>
                {sample.sampleSheet.labWorkbookId ? (
                  <RouterLink
                    to={`/lab/${sample.sampleSheet.labWorkbookId}`}
                    style={{ fontWeight: 600 }}
                  >
                    {sample.sampleSheet.name ?? 'View sheet'} ({sample.sampleSheet.id})
                  </RouterLink>
                ) : (
                  <Text color="gray.600">
                    {sample.sampleSheet.name ?? 'Sample sheet'} ({sample.sampleSheet.id})
                  </Text>
                )}
                {sample.sampleSheet.labSheetUrl ? (
                  <Text mt={1} color="gray.600">
                    Legacy sheet:{' '}
                    <a href={sample.sampleSheet.labSheetUrl} target="_blank" rel="noreferrer">
                      {sample.sampleSheet.labSheetUrl}
                    </a>
                  </Text>
                ) : null}
              </Box>
            ) : null}

            <SampleEditForm
              sampleId={sample.id}
              environmentType={sample.samplingPoint.type as SampleType}
              defaults={{
                number: sample.number,
                bioRep: (sample.bioRep as BioRep | null) ?? null,
                time: sampleTimeToDateInput(sample.time ?? null) ?? null,
                status: (sample.status as SampleStatus | null) ?? null,
                primDnaExtractionKit: sample.primDnaExtractionKit ?? null,
                primDilutedDnaVolume: sample.primDilutedDnaVolume ?? null,
                primDilutedDnaConcentration: sample.primDilutedDnaConcentration ?? null,
                primDilutedDnaQuality280: sample.primDilutedDnaQuality280 ?? null,
                primDilutedDnaQuality230: sample.primDilutedDnaQuality230 ?? null,
                primElutedDnaQuality280: sample.primElutedDnaQuality280 ?? null,
                primElutedDnaQuality230: sample.primElutedDnaQuality230 ?? null,
                primElutedDnaConcentration: sample.primElutedDnaConcentration ?? null,
                primElutedDnaVolume: sample.primElutedDnaVolume ?? null,
                filteredVolume: sample.filteredVolume ?? null,
                bod: sample.bod ?? null,
                suspendedSolids: sample.suspendedSolids ?? null,
                operatedFlowRate: sample.operatedFlowRate ?? null,
                flowRate: sample.flowRate ?? null,
              }}
            />

            {/* Projects moved below form as bullet list */}
          </>
        ) : (
          <Text color="gray.600">Sample not found.</Text>
        )}
      </Stack>

      {sample ? (
        <Box mt={theme.spacing[6]}>
          <Heading size="md" mb={2} mt={2}>
            Projects
          </Heading>
          {sample.projects.length ? (
            <ul style={{ listStyleType: 'disc', paddingLeft: theme.spacing[4] }}>
              {sample.projects.map(p => (
                <li key={p.id} style={{ marginBottom: theme.spacing[1] }}>
                  <RouterLink
                    to={`/research/${p.id}?samplingPoints=${sample.samplingPoint.id}`}
                    style={{ fontWeight: 600 }}
                  >
                    {p.name} ({p.id})
                  </RouterLink>
                </li>
              ))}
            </ul>
          ) : (
            <Text color="gray.600">No linked projects.</Text>
          )}
        </Box>
      ) : null}

      {sample ? (
        <Box mt={theme.spacing[6]}>
          <Heading size="md" mb={2} mt={2}>
            Organisations with access
          </Heading>
          {isSampleAccessPending ? (
            <Text color="gray.600">Loading organisations...</Text>
          ) : organisationsWithAccess.length > 0 ? (
            <ul style={{ listStyleType: 'disc', paddingLeft: theme.spacing[4] }}>
              {organisationsWithAccess.map(org => (
                <li key={org.id} style={{ marginBottom: theme.spacing[1] }}>
                  <RouterLink to={`/admin/organisation/${org.id}`} style={{ fontWeight: 600 }}>
                    {org.name} ({org.id})
                  </RouterLink>
                  <Text as="span" color="gray.600" ml={2}>
                    {org.accessLevel === AccessLevel.OWNER ? '(O)' : '(R)'}
                  </Text>
                </li>
              ))}
            </ul>
          ) : (
            <Text color="gray.600">No organisations have access.</Text>
          )}
        </Box>
      ) : null}

      {sample ? (
        <Box mt={theme.spacing[6]}>
          <Heading size="md" mb={2} mt={2}>
            Laboratory sheets and chips
          </Heading>
          {chips && chips.length > 0 ? (
            <ul style={{ listStyleType: 'disc', paddingLeft: theme.spacing[4] }}>
              {chain(chips)
                .groupBy(c => c.labSheetUrl || 'NO_SHEET')
                .toPairs()
                .map(([labSheetUrl, arr]) => (
                  <li key={labSheetUrl} style={{ marginBottom: theme.spacing[2] }}>
                    {labSheetUrl !== 'NO_SHEET' ? (
                      // External lab sheet link
                      <a href={labSheetUrl} target="_blank" rel="noreferrer" style={{ fontWeight: 600 }}>
                        Lab sheet
                      </a>
                    ) : (
                      <Text as="span" color="gray.600">
                        No lab sheet
                      </Text>
                    )}
                    <ul style={{ listStyleType: 'circle', paddingLeft: theme.spacing[4], marginTop: 4 }}>
                      {arr.map(ch => {
                        const base =
                          sample.number != null && sample.bioRep
                            ? `${sample.id}-${sample.number}${sample.bioRep}`
                            : null;
                        const to = base
                          ? `/chip/${ch.id}?samples=${base}-rep1,${base}-rep2,${base}-rep3`
                          : `/chip/${ch.id}`;
                        return (
                          <li key={ch.id}>
                            <RouterLink to={to}>
                              {ch.name} {ch.runDate ? `(${new Date(ch.runDate).toISOString().slice(0, 10)})` : ''}
                            </RouterLink>
                          </li>
                        );
                      })}
                    </ul>
                  </li>
                ))
                .value()}
            </ul>
          ) : (
            <Text color="gray.600">No lab sheets or chips.</Text>
          )}
        </Box>
      ) : null}

      <MaybeApiError error={error ?? sampleAccessError} />
    </div>
  );
}

function SampleEditForm({
  sampleId,
  environmentType,
  defaults,
}: {
  sampleId: number;
  environmentType: SampleType;
  defaults: Partial<PatchSampleRequest>;
}) {
  const patchSample = usePatchSample();
  const defaultValues: Partial<PatchSampleRequest> = { id: sampleId, ...defaults };
  const isEditable = defaults.status === SampleStatus.SUBMITTED || defaults.status === SampleStatus.UNAPPROVED;
  const isWater = isWaterType(environmentType);

  const {
    handleSubmit,
    register,
    control,
    formState: { errors, isSubmitting },
  } = useForm<PatchSampleRequest>({
    resolver: zodResolver(zPatchSampleRequest),
    defaultValues,
  });

  const onSubmit = async (values: PatchSampleRequest) => {
    await patchSample.mutateAsync(values);
  };

  const numberField = (
    name: Path<PatchSampleRequest>,
    label: string,
    step = 'any',
    validate?: (value: unknown) => true | string,
    error?: string,
    disabled?: boolean,
  ) => {
    return (
      <FormControl isInvalid={!!error}>
        <FormLabel>{label}</FormLabel>
        <Input
          type="number"
          step={step}
          isDisabled={!!disabled}
          {...register(name, {
            setValueAs: v => (v === '' || v === undefined || v === null ? null : Number(v)),
            validate,
          })}
        />
        <FormErrorMessage>{error}</FormErrorMessage>
      </FormControl>
    );
  };

  return (
    <form onSubmit={e => void handleSubmit(onSubmit)(e)}>
      <Stack spacing={4}>
        <HStack>
          <Box flex="1">
            <FormControl>
              <FormLabel>Number</FormLabel>
              <Input isReadOnly value={defaults.number ?? ''} type="number" step="1" />
            </FormControl>
          </Box>
          <Box flex="1">
            <FormControl>
              <FormLabel>BioRep</FormLabel>
              <Input isReadOnly value={defaults.bioRep ?? ''} />
            </FormControl>
          </Box>
        </HStack>
        <HStack>
          <FormControl isInvalid={!!errors.time}>
            <FormLabel>Date</FormLabel>
            <Input type="date" isDisabled={!isEditable} {...register('time')} />
            <FormErrorMessage>{errors.time?.message}</FormErrorMessage>
          </FormControl>
          <FormControl>
            <Box>
              <ControlledSelect
                control={control}
                name="status"
                options={Object.values(SampleStatus)}
                placeholder="Status"
                labeller={v => String(v)}
              />
            </Box>
          </FormControl>
        </HStack>
        <FormControl isInvalid={!!errors.primDnaExtractionKit}>
          <FormLabel>DNA Extraction Kit</FormLabel>
          <Input isDisabled={!isEditable} {...register('primDnaExtractionKit')} />
          <FormErrorMessage>{errors.primDnaExtractionKit?.message}</FormErrorMessage>
        </FormControl>
        <Heading size="sm">DNA Measurements — Diluted</Heading>
        <HStack>
          {numberField(
            'primDilutedDnaVolume',
            'Diluted DNA Volume (µL)',
            'any',
            undefined,
            errors.primDilutedDnaVolume?.message,
            !isEditable,
          )}
          {numberField(
            'primDilutedDnaConcentration',
            'Diluted DNA Concentration (ng/µL)',
            'any',
            undefined,
            errors.primDilutedDnaConcentration?.message,
            !isEditable,
          )}
        </HStack>
        <HStack>
          {numberField(
            'primDilutedDnaQuality280',
            'Diluted DNA 260/280',
            'any',
            undefined,
            errors.primDilutedDnaQuality280?.message,
            !isEditable,
          )}
          {numberField(
            'primDilutedDnaQuality230',
            'Diluted DNA 260/230',
            'any',
            undefined,
            errors.primDilutedDnaQuality230?.message,
            !isEditable,
          )}
        </HStack>
        <Heading size="sm">DNA Measurements — Eluted</Heading>
        <HStack>
          {numberField(
            'primElutedDnaVolume',
            'Eluted DNA Volume (µL)',
            'any',
            undefined,
            errors.primElutedDnaVolume?.message,
            !isEditable,
          )}
          {numberField(
            'primElutedDnaConcentration',
            'Eluted DNA Concentration (ng/µL)',
            'any',
            undefined,
            errors.primElutedDnaConcentration?.message,
            !isEditable,
          )}
        </HStack>
        <HStack>
          {numberField(
            'primElutedDnaQuality280',
            'Eluted DNA 260/280',
            'any',
            undefined,
            errors.primElutedDnaQuality280?.message,
            !isEditable,
          )}
          {numberField(
            'primElutedDnaQuality230',
            'Eluted DNA 260/230',
            'any',
            undefined,
            errors.primElutedDnaQuality230?.message,
            !isEditable,
          )}
        </HStack>
        <Heading size="sm">Water/Wastewater</Heading>
        <HStack>
          {numberField(
            'filteredVolume',
            'Filtered Volume (mL)',
            'any',
            val => {
              const v = val as number | null | undefined;
              if (!isWater || !isEditable) return true;
              return v != null ? true : 'Required for water samples';
            },
            errors.filteredVolume?.message,
            !isEditable || !isWater,
          )}
          {numberField('bod', 'BOD (mg/L)', 'any', undefined, errors.bod?.message, !isEditable || !isWater)}
        </HStack>
        <HStack>
          {numberField(
            'suspendedSolids',
            'Suspended Solids (mg/L)',
            'any',
            undefined,
            errors.suspendedSolids?.message,
            !isEditable || !isWater,
          )}
          {numberField(
            'operatedFlowRate',
            'Operated Flow Rate (L/h)',
            'any',
            undefined,
            errors.operatedFlowRate?.message,
            !isEditable || !isWater,
          )}
          {numberField(
            'flowRate',
            'Flow Rate (L/h)',
            'any',
            undefined,
            errors.flowRate?.message,
            !isEditable || !isWater,
          )}
        </HStack>
        <Button
          type="submit"
          colorScheme="blue"
          isLoading={patchSample.isPending || isSubmitting}
          isDisabled={Object.keys(errors).length > 0}
        >
          Save changes
        </Button>
        <MaybeApiError error={patchSample.error} />
      </Stack>
    </form>
  );
}

function sampleTimeToDateInput(time?: Date | string | null): string | null {
  if (!time) return null;
  const d = typeof time === 'string' ? new Date(time) : time;
  return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())).toISOString().slice(0, 10);
}
